import { sorted } from "helpers";
import type { SensorTopology } from "src/store/api/generatedApi";
import { activeConnections, activeInstallations } from "./topologyEditing";

export type TopologyDeviceOrder = "relationships" | "alphabetical";

interface DeviceRelationships {
  device: SensorTopology["devices"][number];
  sensorIds: Set<number>;
  locations: Set<string>;
}

const sharedCount = <T>(left: Set<T>, right: Set<T>) =>
  [...left].filter((identifier) => right.has(identifier)).length;

const locationKey = (locations: Set<string>) =>
  locations.size === 0
    ? "1:"
    : `0:${sorted([...locations], (name) => name).join("\0")}`;

const connectedGroup = (
  group: DeviceRelationships[],
  candidates: DeviceRelationships[]
): DeviceRelationships[] => {
  const neighbours = candidates.filter(
    (candidate) =>
      !group.includes(candidate) &&
      group.some(
        (member) => sharedCount(member.sensorIds, candidate.sensorIds) > 0
      )
  );
  return neighbours.length === 0
    ? group
    : connectedGroup([...group, ...neighbours], candidates);
};

const orderNeighbours = (
  remaining: DeviceRelationships[],
  previous: DeviceRelationships
): DeviceRelationships[] => {
  const [next, ...others] = sorted(remaining, (candidate) => [
    -sharedCount(previous.sensorIds, candidate.sensorIds),
    -sharedCount(previous.locations, candidate.locations),
  ]);
  return next == null ? [] : [next, ...orderNeighbours(others, next)];
};

export const orderTopologyDevices = (
  topology: SensorTopology,
  order: TopologyDeviceOrder
) => {
  const alphabetical = sorted(topology.devices, (device) => device.name);
  if (order === "alphabetical") return alphabetical;
  const connections = activeConnections(topology);
  const installations = activeInstallations(topology);
  const profiles = alphabetical.map((device): DeviceRelationships => {
    const sensorIds = new Set(
      connections
        .filter((connection) => connection.device_id === device.id)
        .map((connection) => connection.sensor_id)
    );
    return {
      device,
      sensorIds,
      locations: new Set(
        installations
          .filter((installation) => sensorIds.has(installation.sensor_id))
          .map((installation) => installation.location_name)
      ),
    };
  });
  const groups = profiles.reduce<DeviceRelationships[][]>(
    (groups, profile) =>
      groups.some((group) => group.includes(profile))
        ? groups
        : [...groups, connectedGroup([profile], profiles)],
    []
  );
  return sorted(groups, (group) =>
    locationKey(new Set(group.flatMap((profile) => [...profile.locations])))
  )
    .flatMap((group) => {
      const [first, ...remaining] = sorted(
        group,
        (profile) => `${locationKey(profile.locations)}\0${profile.device.name}`
      );
      return first == null ? [] : [first, ...orderNeighbours(remaining, first)];
    })
    .map((profile) => profile.device);
};
